import { Chip, Switch, Tooltip } from "@heroui/react";

import { ImageTile } from "@/components/kit";
import { ScrapedProduct } from "@/types/scraped-product";
import { formatFullDate, getRelativeTime } from "@/utils/date";

/** Names of the scraped category and brand that are blacklisted, which blocks whitelisting and importing the product. */
export const blacklistedRelatives = (product: ScrapedProduct): string[] => [
  ...(product.scrapedCategory?.blacklist ? ["Category"] : []),
  ...(product.scrapedBrand?.blacklist ? ["Brand"] : []),
];

export const isPrimaryLink = (product: ScrapedProduct) =>
  Boolean(product.product) && product.product!.scrapedProductId === product.id;

export function ProductCell({
  product,
  onImageClick,
}: {
  product: ScrapedProduct;
  onImageClick: () => void;
}) {
  const imageCount = product.images?.length ?? 0;

  return (
    <div className="flex min-w-0 items-center gap-3">
      <button
        aria-label={product.name}
        className="relative shrink-0 cursor-(--cursor-interactive) rounded-xl"
        type="button"
        onClick={onImageClick}
      >
        <ImageTile src={product.images?.[0]} />
        {imageCount > 1 ? (
          <span className="bg-surface text-muted shadow-surface absolute -right-1 -bottom-1 rounded-full px-1.5 text-[10px] leading-4 font-medium tabular-nums">
            +{imageCount - 1}
          </span>
        ) : null}
      </button>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate font-medium" title={product.name}>
          {product.name}
        </span>
        <span className="text-muted flex min-w-0 items-center gap-1 text-xs">
          <span className="truncate font-mono">{product.externalId}</span>
          <span aria-hidden="true">·</span>
          <span className="truncate">{product.supplier.name}</span>
        </span>
      </div>
    </div>
  );
}

export function PriceCell({ product }: { product: ScrapedProduct }) {
  return (
    <div className="flex flex-col items-end leading-tight">
      <span className="font-medium whitespace-nowrap tabular-nums">
        AED {product.price.toFixed(2)}
      </span>
      {product.stockAvailability ? (
        <span className="text-muted text-xs">In stock</span>
      ) : (
        <span className="text-xs text-(--danger)">Out of stock</span>
      )}
    </div>
  );
}

const EntityLink = ({
  name,
  href,
  blacklisted,
  empty,
}: {
  name?: string;
  href: string;
  blacklisted?: boolean;
  empty: string;
}) =>
  name ? (
    <a
      className={`block max-w-full truncate hover:underline ${
        blacklisted ? "text-(--danger)" : ""
      }`}
      href={href}
      rel="noreferrer"
      target="_blank"
      title={blacklisted ? `${name} (blacklisted)` : name}
    >
      {name}
    </a>
  ) : (
    <span className="text-muted">{empty}</span>
  );

export function SourceCell({ product }: { product: ScrapedProduct }) {
  return (
    <div className="flex min-w-0 flex-col leading-tight">
      <EntityLink
        blacklisted={product.scrapedCategory?.blacklist}
        empty="No category"
        href={`/scraped/categories?name=${encodeURIComponent(product.scrapedCategory?.name ?? "")}`}
        name={product.scrapedCategory?.name}
      />
      <span className="text-xs">
        <EntityLink
          blacklisted={product.scrapedBrand?.blacklist}
          empty="No brand"
          href={`/scraped/brands?name=${encodeURIComponent(product.scrapedBrand?.name ?? "")}`}
          name={product.scrapedBrand?.name}
        />
      </span>
    </div>
  );
}

export function LinkCell({
  product,
  onFilterByLinkedProduct,
}: {
  product: ScrapedProduct;
  onFilterByLinkedProduct: () => void;
}) {
  const related = blacklistedRelatives(product);

  if (!product.product) {
    return (
      <div className="flex flex-col items-start gap-0.5">
        <Chip variant="soft">Not linked</Chip>
        {product.blacklist ? (
          <span className="text-xs text-(--danger)">Blacklisted</span>
        ) : related.length > 0 ? (
          <span className="text-xs text-(--danger)">
            {related.join(" & ")} blacklisted
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col items-start gap-0.5">
      <span className="flex items-center gap-2">
        <Chip color="success" variant="soft">
          Linked
        </Chip>
        <span className="text-muted text-xs">
          {isPrimaryLink(product) ? "Primary" : "Secondary"}
        </span>
      </span>
      <Tooltip delay={400}>
        <button
          className="text-muted block max-w-full cursor-(--cursor-interactive) truncate text-xs underline decoration-dotted underline-offset-2 hover:text-(--foreground)"
          type="button"
          onClick={onFilterByLinkedProduct}
        >
          {product.product.name}
        </button>
        <Tooltip.Content>Filter by this linked product</Tooltip.Content>
      </Tooltip>
    </div>
  );
}

export function ActivityCell({ product }: { product: ScrapedProduct }) {
  return (
    <div className="flex flex-col leading-tight">
      <span title={formatFullDate(product.lastSeenAt)}>
        {getRelativeTime(product.lastSeenAt)}
      </span>
      <span
        className="text-muted text-xs"
        title={formatFullDate(product.createdAt)}
      >
        Scraped {getRelativeTime(product.createdAt)}
      </span>
    </div>
  );
}

export function WhitelistCell({
  product,
  onChange,
}: {
  product: ScrapedProduct;
  onChange: (blacklist: boolean) => void;
}) {
  const related = blacklistedRelatives(product);
  const isDisabled = related.length > 0;
  const hint = isDisabled
    ? `${related.join(" and ")} is blacklisted`
    : product.blacklist
      ? "Blacklisted - turn on to whitelist"
      : "Whitelisted - turn off to blacklist";

  return (
    <Tooltip delay={400}>
      <span className="inline-flex">
        <Switch
          aria-label={`Whitelisted: ${product.name}`}
          isDisabled={isDisabled}
          isSelected={!product.blacklist}
          size="sm"
          onChange={(selected) => onChange(!selected)}
        >
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      </span>
      <Tooltip.Content>{hint}</Tooltip.Content>
    </Tooltip>
  );
}
